/**
 * Update History Service
 *
 * Manages writing EID web update session history to Firestore.
 * Creates a document when EID is validated and updates it at each step.
 */

import {
  addDoc,
  collection,
  doc,
  Firestore,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { ErrorCode, WizardStep } from '../types/wizard-types';

/**
 * Document structure for /devices collection
 */
export interface DeviceUpdateDocument {
  orgId: string;
  eid: string;
  userId: string;
  lastUpdated: Timestamp;
  failed: boolean;
  failureReason?: ErrorCode;
  /** Current step in the wizard */
  currentStep: WizardStep;
  /** Timestamp when the session started */
  startedAt: Timestamp;
  /** Timestamp when the session completed (success or failure) */
  completedAt?: Timestamp;
}

/**
 * Update History Service
 *
 * Tracks device update sessions in Firestore for analytics and debugging.
 */
export class UpdateHistoryService {
  private sessionDocId: string | null = null;
  private firestore: Firestore | null = null;
  private orgId: string | null = null;
  private userId: string | null = null;
  private isTerminal: boolean = false;

  /**
   * Initialize the service with user context
   */
  initialize(firestore: Firestore, orgId: string, userId: string): void {
    this.firestore = firestore;
    this.orgId = orgId;
    this.userId = userId;
  }

  /**
   * Check if the service is initialized with user context
   */
  isInitialized(): boolean {
    return !!(this.firestore && this.orgId && this.userId);
  }

  /**
   * Get the current session document ID
   */
  getSessionId(): string | null {
    return this.sessionDocId;
  }

  /**
   * Start a new update session when EID is validated
   * Creates a new document in the /devices collection
   */
  async startSession(eid: string): Promise<string> {
    if (!this.firestore || !this.orgId || !this.userId) {
      throw new Error('UpdateHistoryService not initialized');
    }

    const devicesCollection = collection(this.firestore, 'devices');

    const docData: Omit<DeviceUpdateDocument, 'lastUpdated' | 'startedAt'> & {
      lastUpdated: ReturnType<typeof serverTimestamp>;
      startedAt: ReturnType<typeof serverTimestamp>;
    } = {
      orgId: this.orgId,
      eid,
      userId: this.userId,
      lastUpdated: serverTimestamp(),
      startedAt: serverTimestamp(),
      failed: false,
      currentStep: WizardStep.EID_ENTRY,
    };

    const docRef = await addDoc(devicesCollection, docData);
    this.sessionDocId = docRef.id;

    return this.sessionDocId;
  }

  /**
   * Update the session with current step progress
   */
  async updateStep(step: WizardStep): Promise<void> {
    if (!this.sessionDocId || !this.firestore) {
      return;
    }

    const docRef = doc(this.firestore, 'devices', this.sessionDocId);

    await updateDoc(docRef, {
      currentStep: step,
      lastUpdated: serverTimestamp(),
    });
  }

  /**
   * Mark the session as failed with an error code
   */
  async markFailed(errorCode: ErrorCode): Promise<void> {
    if (!this.sessionDocId || !this.firestore) {
      return;
    }
    if (this.isTerminal) {
      return;
    }

    this.isTerminal = true;
    const docRef = doc(this.firestore, 'devices', this.sessionDocId);

    await updateDoc(docRef, {
      failed: true,
      failureReason: errorCode,
      lastUpdated: serverTimestamp(),
      completedAt: serverTimestamp(),
    });
  }

  /**
   * Mark the session as successfully completed
   */
  async markCompleted(): Promise<void> {
    if (!this.sessionDocId || !this.firestore) {
      return;
    }
    if (this.isTerminal) {
      return;
    }

    this.isTerminal = true;
    const docRef = doc(this.firestore, 'devices', this.sessionDocId);

    await updateDoc(docRef, {
      failed: false,
      currentStep: WizardStep.FINALIZATION,
      lastUpdated: serverTimestamp(),
      completedAt: serverTimestamp(),
    });
  }

  /**
   * Reset the service state for a new session
   */
  reset(): void {
    this.sessionDocId = null;
    this.isTerminal = false;
  }
}
